import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { ImportCommitResult, ImportPreview } from '@/api/clientImports/clientImportsApi.types';
import { resolveCommitErrorMessage } from '@/api/clientImports/resolveCommitErrorMessage';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { AdminPageHeader } from '@/components/admin/shared/AdminPageHeader';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/common/Icon';
import { useCommitImportMutation } from '@/hooks/useCommitImportMutation';
import { useValidateImportMutation } from '@/hooks/useValidateImportMutation';
import { showErrorNotification } from '@/utils/notifications';
import { CommitBar } from './CommitBar';
import { FileUploadPanel } from './FileUploadPanel';
import { ImportPreviewGrid } from './ImportPreviewGrid';
import { ImportResultPanel } from './ImportResultPanel';
import { ImportSummaryBar } from './ImportSummaryBar';
import { IssueLegend } from './IssueLegend';
import { TemplateDownloadCard } from './TemplateDownloadCard';

type ImportStep = 'upload' | 'review' | 'done';

function generateIdempotencyKey(): string {
  return crypto.randomUUID();
}

export function ClientImportPage() {
  const { t } = useTranslation('clientImport');
  const navigate = useNavigate();
  const validateMutation = useValidateImportMutation();
  const commitMutation = useCommitImportMutation();

  const [step, setStep] = useState<ImportStep>('upload');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [commitResult, setCommitResult] = useState<ImportCommitResult | null>(null);

  function handleFileSelected(file: File) {
    validateMutation.mutate(file, {
      onSuccess: function handlePreview(result) {
        setPreview(result);
        setIdempotencyKey(generateIdempotencyKey());
        setStep('review');
      },
      onError: function notifyValidateError(validateError) {
        const backendMessage = resolveApiErrorMessage(validateError);
        showErrorNotification(backendMessage.length > 0 ? backendMessage : t('upload.error'));
      },
    });
  }

  function handleCommit() {
    if (preview === null) {
      return;
    }
    commitMutation.mutate(
      { rows: preview.rows, idempotencyKey },
      {
        onSuccess: function handleCommitSuccess(result) {
          setCommitResult(result);
          setStep('done');
        },
        onError: function notifyCommitError(commitError) {
          const detailMessage = resolveCommitErrorMessage(t, commitError);
          const backendMessage = resolveApiErrorMessage(commitError);
          showErrorNotification(
            detailMessage.length > 0
              ? detailMessage
              : backendMessage.length > 0
                ? backendMessage
                : t('commitBar.commitError')
          );
        },
      }
    );
  }

  function handleImportAnother() {
    setPreview(null);
    setCommitResult(null);
    setIdempotencyKey('');
    setStep('upload');
  }

  return (
    <div className="p-6 md:p-8">
      <AdminPageHeader
        title={t('page.title')}
        subtitle={t('page.subtitle')}
        action={
          <Button
            variant="ghost"
            size="small"
            className="gap-1.5"
            onClick={function goBack() {
              navigate(APPLICATION_ROUTES.workspaceClients);
            }}
          >
            <Icon name="ArrowLeft" />
            {t('page.backToClients')}
          </Button>
        }
      />

      {step === 'upload' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <TemplateDownloadCard />
          <FileUploadPanel
            isChecking={validateMutation.isPending}
            onFileSelected={handleFileSelected}
          />
        </div>
      ) : null}

      {step === 'review' && preview !== null ? (
        <div className="flex flex-col gap-4">
          <ImportSummaryBar summary={preview.summary} />
          <IssueLegend />
          <ImportPreviewGrid columns={preview.columns} rows={preview.rows} />
          <CommitBar
            isCommittable={preview.summary.isCommittable}
            isCommitting={commitMutation.isPending}
            onCommit={handleCommit}
          />
        </div>
      ) : null}

      {step === 'done' && commitResult !== null ? (
        <ImportResultPanel result={commitResult} onImportAnother={handleImportAnother} />
      ) : null}
    </div>
  );
}
